<template>
  <e-dialog ref="dialogRef" @after-leave="handleDialogAfterLeave" @open="handleDialogOpen">
    <template #activator>
      <slot name="activator" />
    </template>

    <template #title>
      <div class="h3">{{ selectedRoomCategory?.name[0]?.value || 'Новая категория номера' }}</div>
    </template>

    <e-form
      v-if="!selectedRoomCategory || isEdit"
      v-model="form.isValid"
      class="d-flex flex-column row-gap-6"
      @submit="handleSubmit"
    >
      <e-autocomplete
        v-model="form.type"
        :items="typeSuggestions"
        item-title="name"
        label="Тип"
        return-object
        show-menu-icon
        :rules="[required()]"
        :disabled="isEdit && !selectedRoomCategory?.operations.fullEdit"
        :loading="isTypeSuggestionsLoading"
        @update:search="getTypeSuggestions"
      />
      <multilanguage-field
        v-model="form.name"
        :rules="[required()]"
        :common-rules="[maxLength(255)]"
        label="Наименование"
      />
      <multilanguage-field
        v-model="form.description"
        :rules="[required()]"
        :common-rules="[maxLength(255)]"
        textarea
        label="Описание"
      />
      <e-input
        v-model="form.places"
        class="narrow-field"
        label="Основных мест"
        :maska="placesMask"
        :rules="[required(), min(1, 'Минимум — 1 место'), max(5, 'Максимум — 5 мест')]"
        :disabled="isEdit && !selectedRoomCategory?.operations.fullEdit"
      />
      <e-input
        v-model="form.addPlaces"
        class="narrow-field"
        label="Дополнительных мест"
        :maska="placesMask"
        :rules="addPlacesRules"
      />
      <div class="d-flex justify-end">
        <e-btn variant="secondary" class="mr-4" @click="handleCancel"> Отмена</e-btn>
        <e-btn variant="primary" type="submit" :loading="form.isLoading">
          {{ isEdit ? 'Сохранить' : 'Создать' }}
        </e-btn>
      </div>
    </e-form>

    <div v-else-if="selectedRoomCategory" class="d-flex flex-column row-gap-6">
      <property-item title="Тип">
        {{ selectedRoomCategory.type.name }}
      </property-item>

      <property-item title="Наименование">
        <multilanguage-text :text="selectedRoomCategory.name">
          <template #default="{ value }">
            <div class="paragraph-m_h font-600">{{ value }}</div>
          </template>
        </multilanguage-text>
      </property-item>

      <property-item title="Описание">
        <multilanguage-text :text="selectedRoomCategory.description">
          <template #default="{ value }">
            <div class="paragraph-m_h font-600">{{ value }}</div>
          </template>
        </multilanguage-text>
      </property-item>

      <div class="d-flex">
        <property-item title="Основных мест" class="mr-10">
          {{ selectedRoomCategory.seat.baseQuantity }}
        </property-item>
        <property-item title="Дополнительных мест">
          {{ selectedRoomCategory.seat.extQuantity }}
        </property-item>
      </div>
      <div class="d-flex">
        <remove-room-category-dialog
          v-if="!isOperationHidden(selectedRoomCategory.operations.delete)"
          :room-category-id="selectedRoomCategory.cid"
          :disabled="isOperationDisabled(selectedRoomCategory.operations.delete)"
          @remove-success="handleRemoveSuccess"
        >
          <e-btn
            variant="ghost"
            class="text-red"
            prepend-icon-src="trash"
            :disabled="isOperationDisabled(selectedRoomCategory.operations.delete)"
          >
            Удалить
          </e-btn>
        </remove-room-category-dialog>
        <e-btn
          v-if="!isOperationHidden(selectedRoomCategory.operations.edit)"
          class="ml-auto"
          variant="primary"
          prepend-icon-src="editPen"
          :loading="isEditRoomCategoryStartLoading"
          :disabled="isOperationDisabled(selectedRoomCategory.operations.edit)"
          @click="handleEdit"
        >
          Редактировать
        </e-btn>
      </div>
    </div>
  </e-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import type { MultilanguageValue } from '@/core/utils/languages';
import type { RoomCategoriesItemType } from '@/parts/room-categories/store/roomCategories';
import type { RoomInfoType } from '@/parts/room-categories/types';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EAutocomplete, EBtn, EForm, EInput, MultilanguageField, MultilanguageText } from '@/core/components/ui';
import { useSuggestions } from '@/core/composables/suggestions';
import { useSnackbar } from '@/core/composables/useSnackbar';
import { placesMask } from '@/core/consts/masks';
import { getDefaultMultilanguageValues } from '@/core/utils/languages';
import { deepCloneObject } from '@/core/utils/object';
import { isOperationDisabled, isOperationHidden } from '@/core/utils/permissions';
import { max, maxLength, min, required } from '@/core/utils/validation';
import RemoveRoomCategoryDialog from '@/parts/room-categories/components/RemoveRoomCategoryDialog.vue';
import { useRoomCategoriesStore } from '@/parts/room-categories/store/roomCategories';

const {
  setRoomCategoryById,
  addRoomCategory,
  editRoomCategoryStart,
  isEditRoomCategoryStartLoading,
  editRoomCategoryCancel,
  editRoomCategoryUpdate,
} = useRoomCategoriesStore();

const props = defineProps<{
  roomCategory?: RoomCategoriesItemType;
  editImmediate?: boolean;
}>();

const emit = defineEmits<{
  (event: 'success:add', data: RoomCategoriesItemType): void;
  (event: 'success:update', data: RoomCategoriesItemType): void;
  (event: 'success:remove'): void;
  (event: 'after-leave'): void;
}>();

const { showSnackbar } = useSnackbar();
const { roomTypeSuggestions } = useSuggestions();

const {
  isLoading: isTypeSuggestionsLoading,
  suggestions: typeSuggestions,
  getSuggestions: getTypeSuggestions,
} = roomTypeSuggestions();

const dialogRef = ref();
const isEdit = ref(false);

const selectedRoomCategory = ref<RoomCategoriesItemType>();

const form = reactive<{
  type: RoomInfoType | null;
  name: MultilanguageValue[];
  description: MultilanguageValue[];
  places: string | null;
  addPlaces: string | null;

  isValid: boolean;
  isLoading: boolean;
}>({
  type: null,
  name: getDefaultMultilanguageValues(),
  description: getDefaultMultilanguageValues(),
  places: null,
  addPlaces: null,

  isValid: false,
  isLoading: false,
});

const addPlacesRules = computed(() => {
  const rules = [max(3, 'Максимум — 3 места')];

  if (isEdit.value && !selectedRoomCategory.value?.operations.fullEdit) {
    const errorMessage = 'Новое значение не может быть меньше установленного';

    rules.push(required(errorMessage));
    rules.push(min(selectedRoomCategory.value?.seat.extQuantity, errorMessage));
  }

  return rules;
});

const handleDialogOpen = () => {
  if (props.roomCategory) {
    selectedRoomCategory.value = props.roomCategory;
  }

  getTypeSuggestions();
};

const handleSubmit = async () => {
  if (!form.isValid) return;

  form.isLoading = true;

  const payload = {
    name: form.name,
    description: form.description,
    roomTypeCid: form.type!.cid,
    seat: {
      baseQuantity: Number(form.places),
      extQuantity: Number(form.addPlaces),
    },
  };

  if (isEdit.value) {
    const result = await editRoomCategoryUpdate({
      accommodationObjectRoomCategoryCid: selectedRoomCategory.value?.cid ?? '',
      roomCategory: payload,
    });

    if (result?.data) {
      dialogRef.value.close();
      emit('success:update', result.data.updateAccommodationObjectRoomCategory.roomCategory!);

      showSnackbar({
        type: 'success',
        title: 'Категория номеров изменена',
        description: form.name[0].value,
      });
    }
  } else {
    const result = await addRoomCategory(payload);

    if (result?.data) {
      dialogRef.value.close();
      emit('success:add', result.data.addAccommodationObjectRoomCategory.roomCategory!);

      showSnackbar({
        type: 'success',
        title: 'Новая категория номеров добавлена',
        description: form.name[0].value,
      });
    }
  }

  form.isLoading = false;
};

const handleEdit = async () => {
  if (!selectedRoomCategory.value?.cid) return;

  const result = await editRoomCategoryStart(selectedRoomCategory.value.cid);

  const roomCategory = result.data?.editAccommodationObjectRoomCategory.roomCategory;

  if (roomCategory) {
    setRoomCategoryById(selectedRoomCategory.value.cid, roomCategory);
    selectedRoomCategory.value = roomCategory;
    presetForm(roomCategory);
    isEdit.value = true;
  }
};

const handleCancel = () => {
  if (isEdit.value) {
    editRoomCategoryCancel();
    isEdit.value = false;
  } else {
    dialogRef.value.close();
  }
};

const handleDialogAfterLeave = () => {
  if (isEdit.value) {
    editRoomCategoryCancel();
    isEdit.value = false;
  }

  clearForm();

  selectedRoomCategory.value = undefined;

  emit('after-leave');
};

const handleRemoveSuccess = () => {
  dialogRef.value.close();
  emit('success:remove');
};

const presetForm = (data: RoomCategoriesItemType) => {
  form.type = deepCloneObject(data.type);
  form.name = deepCloneObject(data.name);
  form.description = deepCloneObject(data.description);
  form.places = data.seat.baseQuantity;
  form.addPlaces = data.seat.extQuantity;
};

const clearForm = () => {
  form.type = null;
  form.name = getDefaultMultilanguageValues();
  form.description = getDefaultMultilanguageValues();
  form.places = null;
  form.addPlaces = null;
};
</script>

<style scoped lang="scss">
.narrow-field {
  width: 280px;
}
</style>
